<template>
  <div class="organization-page" data-testid="organization-page">
    <div class="organization-page-title-container organization-page-title-row">
      <PageTitle class="organization-page-title-row__title" title="Organization" />
      <HelpMenu />
    </div>
    <OrganizationHorizontalMenu
      :menuItems="menuItems"
      :initialTab="currentTab"
      :count_of_active_employees="sidebarCountStore.employees"
      :count_of_active_positions="sidebarCountStore.positions"
      :count_of_active_roles="readinessProductEnabled ? sidebarCountStore.roles : 0"
      :count_of_active_responsibilities="
        readinessProductEnabled ? sidebarCountStore.responsibilities : 0
      "
      :count_of_active_procedures="0"
      :count_of_active_departments="sidebarCountStore.departments"
      :count_of_active_job_categories="jobCategoriesCount"
      :count_of_active_job_families="jobFamiliesCount"
      @tab-selected="handleTabChange"
    />
    <div class="tab-content" v-if="showTabContent">
      <div
        v-if="isResolvingTab"
        class="organization-tab-skeleton"
        data-testid="organization-tab-skeleton"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading tab"
      >
        <div class="organization-tab-skeleton__bar" />
        <div class="organization-tab-skeleton__bar organization-tab-skeleton__bar--medium" />
        <div class="organization-tab-skeleton__bar organization-tab-skeleton__bar--short" />
      </div>
      <OrganizationDashboardTab
        v-if="currentTab === 'Dashboard'"
        :guideData="dashboardGuideData"
        @guide-closed="handleGuideClose"
        @change-tab="handleTabChange"
        @drilldown="handleDrilldown"
      />
      <OrganizationEmployeesTab
        class="employees-tab-container"
        v-if="currentTab === 'Employees'"
        :employees="employeesData"
        :activeEmployeesWithNoaccess="sidebarCountStore.employeesWithNoAccess"
        :description="employeesDescription"
        :actionLabel="employeesActionLabel"
        @drilldown="handleDrilldown"
      />
      <OrganizationPositionsTab
        class="positions-tab-container"
        v-if="currentTab === 'Positions'"
        :positions="positions"
        :pagination="positionsPagination"
        :loading="positionsLoading"
        :searchText="positionsSearchText"
        :appliedFilters="positionsAppliedFilters"
        :backendPagination="positionsBackendPagination"
        :title="positionsTitle"
        :actionLabel="positionsActionLabel"
        :hideTopSection="positionsHideTopSection"
        :hideActiveNo="positionsHideActiveNo"
        :activeRecordsCount="positionsActiveRecordsCount"
        @drilldown="handleDrilldown"
      />
      <OrganizationRolesTab
        class="role-tab-container"
        v-if="currentTab === 'Roles'"
        :guideData="rolesGuideData"
        @drilldown="handleDrilldown"
        @guide-close="handleGuideCloseForRoles"
        :useNewBadge="true"
      />
      <OrganizationResponsibilitiesTab
        class="responsibilities-tab-container"
        v-if="currentTab === 'Responsibilities'"
        :resCount="responsibilitiesResCount"
        :responsibilities="responsibilities"
        :allResponsibilities="allResponsibilities"
        :pagination="responsibilitiesPagination"
        :loading="responsibilitiesLoading"
        :chatterData="responsibilitiesChatterData"
        :totalChatterData="responsibilitiesTotalChatterData"
        :searchText="responsibilitiesSearchText"
        :appliedFilters="responsibilitiesAppliedFilters"
        :filterGroups="responsibilitiesFilterGroups"
        :statusOptions="responsibilitiesStatusOptions"
        :showSearchBar="responsibilitiesShowSearchBar"
        :totalCount="responsibilitiesTotalCount"
      />
      <div v-if="currentTab === 'Departments'" class="departments-tab-container">
        <OrganizationDepartmentsTable />
      </div>
      <div v-if="currentTab === 'Categories'" class="categories-tab-container">
        <OrganizationJobCategoriesTab @active-count="jobCategoriesCount = $event" />
      </div>
      <div v-if="currentTab === 'Job Families'" class="job-families-tab-container">
        <OrganizationJobFamiliesTab @active-count="jobFamiliesCount = $event" />
      </div>
    </div>
    <!-- Employee Detail shown when drilldown is triggered -->
    <div
      class="drilldown-content"
      v-if="!showTabContent && drilldownData && drilldownData.employeeId"
    >
      <EmployeeDetail
        :key="`employee-${drilldownData.employeeId}`"
        :employeeId="drilldownData.employeeId"
        @backToList="handleBackToList"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, onBeforeUnmount, watch, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import OrganizationHorizontalMenu from "@components/OrganizationHorizontalMenu/OrganizationHorizontalMenu.vue";
import OrganizationDashboardTab from "@components/OrganizationDashboardTab/OrganizationDashboardTab.vue";
import OrganizationEmployeesTab from "@components/OrganizationEmployeesTab/OrganizationEmployeesTab.vue";
import OrganizationPositionsTab from "@components/OrganizationPositionsTab/OrganizationPositionsTab.vue";
import OrganizationRolesTab from "@components/OrganizationRolesTab/OrganizationRolesTab.vue";
import OrganizationResponsibilitiesTab from "@components/OrganizationResponsibilitiesTab/OrganizationResponsibilitiesTab.vue";
import EmployeeDetail from "@components/EmployeeDetail/EmployeeDetail.vue";
// Import SVG images as paths
import Home from "@components/assets/icons/Home.svg";
import Employee from "@components/assets/icons/Employee.svg";
import Position from "@components/assets/icons/Position.svg";
import Role from "@components/assets/icons/Role.svg";
import Responsibility from "@components/assets/icons/Responsibility.svg";
import Department from "@components/assets/department-icon.svg";
import PageTitle from "@components/common/PageTitle.vue";
import HelpMenu from "@components/HelpMenu/HelpMenu.vue";
import OrganizationDepartmentsTable from "@components/OrganizationDepartmentsTable/OrganizationDepartmentsTable.vue";
import OrganizationJobCategoriesTab from "@components/OrganizationJobCategoriesTab/OrganizationJobCategoriesTab.vue";
import OrganizationJobFamiliesTab from "@components/OrganizationJobFamiliesTab/OrganizationJobFamiliesTab.vue";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { useEmployees } from "@/composables/employees/useEmployees";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import { useKnockGuides } from "@/composables/common/useKnockGuides";
import { getListofEmployeesV2 } from "@/api/employeeAPI";
import { getListOfJobCategories } from "@/api/jobCategoryAPI";
import { getListOfJobFamilies } from "@/api/jobFamilyAPI";
import { useNavigateToOrganizationTool } from "@components/webmcp/navigation/useNavigateToOrganizationTool.js";
import { useOrganizationTools } from "@components/webmcp/tools/organization/useOrganizationTools.js";
import { createEmployee, createPosition, getAccountData } from "@/api";
import { LD_FLAG_IDEA_MDP_566, LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import Category from "@components/assets/category-icon.svg";
import JobFamilyIcon from "@components/assets/job-family/layers-three-01.svg";
import {
  buildOrganizationMenuItems,
  findOrganizationTab,
  getOrganizationValidTabs,
  organizationTabSlug,
} from "./organizationMenuItems";
import {
  ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME,
  ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME,
} from "@components/webmcp/navigation/routeConstants";
import {
  getOrganizationEmployeeDefaultQueryTab,
  normalizeOrganizationEmployeeQueryTab,
} from "@/utils/organizationEmployeeDetailTabs";
import { refreshCurrentAccountDetails, resolveProductReadinessEnabled } from "@/utils/accountProductFlags";
import { getAvailableApps } from "@/api/appsAPI";

const instance = getCurrentInstance();
const { proxy } = instance;
const route = useRoute();
const router = useRouter();
const sidebarCountStore = useSidebarCountStore();

const ideaMdp440Enabled = ref(false);
const ideaMdp566Enabled = ref(false);
const ideaMdp581Enabled = ref(false);
const jobCategoriesCount = ref(0);
const jobFamiliesCount = ref(0);
// Account-level settings from Account > Settings > Configuration > Jobs
// (job_categories_on / job_families_on). The Categories/Families tabs must only
// render when the LD rollout flag AND the account setting both allow it.
const jobCategoriesOn = ref(false);
const jobFamiliesOn = ref(false);

const accountProductDetails = computed(() => proxy?.$root?.currentAccountDetails ?? null);
const readinessProductEnabled = computed(() =>
  resolveProductReadinessEnabled(accountProductDetails.value)
);

const getValidTabs = () =>
  getOrganizationValidTabs({
    ideaMdp440Enabled: ideaMdp440Enabled.value,
    ideaMdp566Enabled: ideaMdp566Enabled.value,
    ideaMdp581Enabled: ideaMdp581Enabled.value,
    readinessProductEnabled: readinessProductEnabled.value,
  });

const employees = useEmployees();
const employeesViewState = useEmployeesOrganizationTableViewState();
useNavigateToOrganizationTool({ proxy });
useOrganizationTools({ createEmployee, createPosition, proxy });

const employeesData = ref([]);
const employeesDescription = ref(
  "To effectively manage Readiness and Performance employees need to have access to their stuff"
);
const employeesActionLabel = ref("Apply Filter");
const resolveTabFromRoute = () => {
  // ER-11744 — the Job Category detail route has an :id (not a :tab) param, but
  // still renders the Categories tab. Fall back to Dashboard when the feature is
  // disabled so a deep link can never surface the gated tab.
  if (route?.name === ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME) {
    return ideaMdp566Enabled.value ? "Categories" : "Dashboard";
  }

  // ER-11742/ER-11794 — the Job Family detail route has an :id (not a :tab) param,
  // but still renders the Job Families tab so the breadcrumb can navigate back to
  // the list. Fall back to Dashboard when the feature is disabled.
  if (route?.name === ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME) {
    return ideaMdp581Enabled.value ? "Job Families" : "Dashboard";
  }

  const tabParam = route?.params?.tab;
  if (!tabParam) return "Dashboard";

  const matchedTab = findOrganizationTab(tabParam, getValidTabs());
  if (matchedTab) {
    return matchedTab;
  }

  // Departments / Categories / Job Families routes before flags resolve should not fall back to Dashboard
  if (organizationTabSlug(tabParam) === "departments" && ideaMdp440Enabled.value) {
    return "Departments";
  }
  if (organizationTabSlug(tabParam) === "categories" && ideaMdp566Enabled.value) {
    return "Categories";
  }
  if (organizationTabSlug(tabParam) === "job-families" && ideaMdp581Enabled.value) {
    return "Job Families";
  }

  return "Dashboard";
};

// Tabs whose visibility depends on a LaunchDarkly flag (Departments, Categories,
// Job Families) or on the addressable job category/family detail routes cannot be
// resolved synchronously. For those we show a skeleton — never Dashboard content —
// until the flags resolve (ER-12222: a fresh /organization/:tab mount must not
// flash the Dashboard before the requested tab renders).
const GATED_ORGANIZATION_TAB_SLUGS = new Set(["departments", "categories", "job-families"]);

// Synchronous portion of initial tab resolution, run before first render so a
// deep link like /organization/positions shows Positions immediately.
const resolveInitialTabSynchronously = () => {
  // The Job Category / Job Family detail routes render their gated tab; the
  // decision depends on flags that are not loaded yet.
  if (
    route?.name === ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME ||
    route?.name === ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME
  ) {
    return { tab: null, pending: true };
  }

  const tabParam = route?.params?.tab;
  if (!tabParam) return { tab: "Dashboard", pending: false };

  const slug = organizationTabSlug(tabParam);

  // Tabs that never need a flag. Roles/Responsibilities are readiness-gated, but
  // readiness resolves synchronously from the stored account details.
  const synchronousValidTabs = ["Dashboard", "Employees", "Positions"];
  if (readinessProductEnabled.value) {
    synchronousValidTabs.push("Roles", "Responsibilities");
  }

  const matchedTab = findOrganizationTab(tabParam, synchronousValidTabs);
  if (matchedTab) return { tab: matchedTab, pending: false };

  if (GATED_ORGANIZATION_TAB_SLUGS.has(slug)) {
    return { tab: null, pending: true };
  }

  // Unknown tab → Dashboard (default).
  return { tab: "Dashboard", pending: false };
};

const props = defineProps({
  guide_primary_text: {
    type: String,
    default: "Welcome to exIQtive, the Readiness Management Platform",
  },
  guide_secondary_text: {
    type: String,
    default:
      "As an owner or co-owner of an account you can manage the organization consisting of Positions, Roles, Responsibilities and Employees",
  },
  guideData: {
    type: Object,
    default: null,
  },
  // Employees tab props
  employees: {
    type: Array,
    default: () => [],
  },
  employeesDescription: {
    type: String,
    default: "",
  },
  employeesActionLabel: {
    type: String,
    default: "Apply Filter",
  },
  // Positions tab props
  positions: {
    type: Array,
    default: () => [],
  },
  positionsPagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  positionsLoading: {
    type: Boolean,
    default: false,
  },
  positionsAppliedFilters: {
    type: Array,
    default: () => [],
  },
  positionsSearchText: {
    type: String,
    default: "",
  },
  positionsBackendPagination: {
    type: Boolean,
    default: false,
  },
  positionsTitle: {
    type: String,
    default: "Positions Organization",
  },
  positionsActionLabel: {
    type: String,
    default: "Add Position",
  },
  positionsHideTopSection: {
    type: Boolean,
    default: true,
  },
  positionsHideActiveNo: {
    type: Boolean,
    default: false,
  },
  positionsActiveRecordsCount: {
    type: Number,
    default: null,
  },
  // Responsibilities tab props
  responsibilitiesResCount: {
    type: Number,
    default: 0,
  },
  responsibilities: {
    type: Array,
    default: () => [],
  },
  allResponsibilities: {
    type: Array,
    default: () => [],
  },
  responsibilitiesPagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  responsibilitiesLoading: {
    type: Boolean,
    default: false,
  },
  responsibilitiesChatterData: {
    type: Array,
    default: () => [],
  },
  responsibilitiesTotalChatterData: {
    type: Number,
    default: 0,
  },
  responsibilitiesSearchText: {
    type: String,
    default: "",
  },
  responsibilitiesAppliedFilters: {
    type: Array,
    default: () => [],
  },
  responsibilitiesFilterGroups: {
    type: Array,
    default: () => [],
  },
  responsibilitiesStatusOptions: {
    type: Array,
    default: () => ["draft", "active", "retired", "inactive"],
  },
  responsibilitiesShowSearchBar: {
    type: Boolean,
    default: true,
  },
  responsibilitiesTotalCount: {
    type: Number,
    default: 0,
  },
  // Counts for badges
  count_of_active_employees: {
    type: Number,
    default: 0,
  },
  count_of_active_positions: {
    type: Number,
    default: 0,
  },
  count_of_active_roles: {
    type: Number,
    default: 0,
  },
  count_of_active_responsibilities: {
    type: Number,
    default: 0,
  },
  count_of_active_procedures: {
    type: Number,
    default: 0,
  },
  // Initial tab
  initialTab: {
    type: String,
    default: "Dashboard",
  },
});

const emit = defineEmits(["guide-closed", "tab-changed"]);

const initializeDrilldownState = () => {
  // Only handle employee drilldown mode (roles, positions, responsibilities use standalone routes)
  const employeeIdFromQuery = route?.query?.employeeId;
  const employeeTabFromQuery = route?.query?.employeeTab;

  if (employeeIdFromQuery) {
    return {
      drilldown: {
        employeeId: employeeIdFromQuery,
        tab: normalizeOrganizationEmployeeQueryTab(employeeTabFromQuery),
      },
      showContent: false,
      tab: "Employees",
    };
  }

  // Resolve the initial tab synchronously from the route before first render so a
  // fresh /organization/positions entry never flashes the Dashboard (ER-12222).
  const initial = resolveInitialTabSynchronously();
  isResolvingTab.value = initial.pending;

  return {
    drilldown: null,
    showContent: true,
    tab: initial.tab,
  };
};

const isResolvingTab = ref(false);
const initialState = initializeDrilldownState();
const currentTab = ref(initialState.tab);
const showTabContent = ref(initialState.showContent);
const drilldownData = ref(initialState.drilldown);
const drilldownBreadcrumbs = ref(null); // Store breadcrumbs separately to avoid watcher issues

const rolesGuideData = ref(null);
const dashboardGuideData = ref(null);

const { selectGuide, subscribeToGuides } = useKnockGuides();
let unsubscribeGuide = null;

watch(
  () => ({
    tab: route?.params?.tab,
    jobCategoryDetail: route?.name === ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME,
    jobFamilyDetail: route?.name === ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME,
  }),
  ({ tab: newTab, jobCategoryDetail, jobFamilyDetail }) => {
    if (route?.query?.employeeId) return;

    // ER-11744 — keep the Categories tab mounted while the Job Category detail
    // route is active (the route has an :id, not a :tab, so the tab param is
    // empty here and would otherwise reset us to Dashboard).
    if (jobCategoryDetail) {
      currentTab.value = ideaMdp566Enabled.value ? "Categories" : "Dashboard";
      return;
    }

    // ER-11742/ER-11794 — keep the Job Families tab mounted while the Job Family
    // detail route is active, so the breadcrumb/menu can navigate back to the list.
    if (jobFamilyDetail) {
      currentTab.value = ideaMdp581Enabled.value ? "Job Families" : "Dashboard";
      return;
    }

    if (newTab) {
      const matchedTab = findOrganizationTab(newTab, getValidTabs());
      if (matchedTab) {
        currentTab.value = matchedTab;
      } else if (organizationTabSlug(newTab) === "departments" && ideaMdp440Enabled.value) {
        currentTab.value = "Departments";
      } else if (organizationTabSlug(newTab) === "categories" && ideaMdp566Enabled.value) {
        currentTab.value = "Categories";
      } else if (organizationTabSlug(newTab) === "job-families" && ideaMdp581Enabled.value) {
        currentTab.value = "Job Families";
      } else if (
        organizationTabSlug(newTab) !== "departments" &&
        organizationTabSlug(newTab) !== "categories" &&
        organizationTabSlug(newTab) !== "job-families"
      ) {
        currentTab.value = "Dashboard";
        if (router) {
          router.replace({ name: "NewOrganization", params: { tab: "dashboard" } }).catch(() => {});
        }
      }
    } else {
      currentTab.value = "Dashboard";
    }
  }
);

watch(ideaMdp566Enabled, (enabled) => {
  if (!enabled) {
    if (currentTab.value === "Categories") {
      currentTab.value = "Dashboard";
      if (router) {
        router.replace({ name: "NewOrganization", params: { tab: "dashboard" } }).catch(() => {});
      }
    }
    return;
  }

  if (organizationTabSlug(route?.params?.tab) === "categories" || currentTab.value === "Categories") {
    currentTab.value = "Categories";
  }
});

watch(ideaMdp440Enabled, (enabled) => {
  if (!enabled) {
    if (currentTab.value === "Departments") {
      currentTab.value = "Dashboard";
      if (router) {
        router.replace({ name: "NewOrganization", params: { tab: "dashboard" } }).catch(() => {});
      }
    }
    return;
  }

  if (organizationTabSlug(route?.params?.tab) === "departments" || currentTab.value === "Departments") {
    currentTab.value = "Departments";
  }
});

watch(ideaMdp581Enabled, (enabled) => {
  if (!enabled) {
    if (currentTab.value === "Job Families") {
      currentTab.value = "Dashboard";
      if (router) {
        router.replace({ name: "NewOrganization", params: { tab: "dashboard" } }).catch(() => {});
      }
    }
    return;
  }

  if (
    organizationTabSlug(route?.params?.tab) === "job-families" ||
    currentTab.value === "Job Families"
  ) {
    currentTab.value = "Job Families";
  }
});

watch(readinessProductEnabled, (enabled) => {
  if (enabled) return;
  if (currentTab.value === "Roles" || currentTab.value === "Responsibilities") {
    currentTab.value = "Dashboard";
    if (router) {
      router.replace({ name: "NewOrganization", params: { tab: "dashboard" } }).catch(() => {});
    }
  }
});

const isLoadingEmployees = ref(false);

const loadEmployees = async () => {
  if (isLoadingEmployees.value) return;
  isLoadingEmployees.value = true;

  try {
    await employeesViewState.initializeFilters();

    const fromDashboard = route?.query?.fromDashboard === "true";
    const searchEmployeeName = route?.query?.searchEmployee || "";

    // Commented out: API calls moved to EmployeesOrganizationTable component
    // if (fromDashboard) {
    //   await employeesViewState.removeAllFiltersAndReload();
    //
    //   return;
    // }

    employees.clearEmployeeDynamicFilters();
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
    employees.removeEmployeeFilter("status");
    employees.removeEmployeeFilter("archived");
    employees.removeEmployeeFilter("has_access");
    employees.removeEmployeeFilter("has_assignment");
    employees.setPaginationDataOffset(0);

    employeesViewState.loadFiltersOnReload();

    if (
      !employeesViewState.state.filters.values ||
      employeesViewState.state.filters.values.length === 0
    ) {
      employeesViewState.state.filters.values = ["draft", "active"];
      const filter = await import("@/store/pinia/filters");
      filter.default.setFilters({
        key: employeesViewState.state.filters.key,
        filters: ["draft", "active"],
      });
    }

    await proxy.$nextTick();

    const filtersToApply = employeesViewState.state.filters.values
      .map((f) => {
        if (typeof f === "string") return f;
        if (typeof f === "object" && f.value) return f.value;
        return f;
      })
      .filter(Boolean);

    if (filtersToApply && filtersToApply.length > 0) {
      employeesViewState.state.filters.values = filtersToApply;
    }

    await proxy.$nextTick();

    // Commented out: API calls moved to EmployeesOrganizationTable component
    // if (filtersToApply && filtersToApply.length > 0) {
    //   await employeesViewState.applyFilters(filtersToApply);
    // } else {
    //   await employees.loadAllEmployees();
    // }

    await proxy.$nextTick();
  } catch (error) {
    console.error("Error loading employees:", error);
  } finally {
    isLoadingEmployees.value = false;
  }
};

watch(
  () => employees.allEmployees.value,
  async (newVal) => {
    employeesData.value = newVal || [];
  },
  { deep: true, immediate: true }
);
watch(
  () => ({
    employeeId: route?.query?.employeeId,
    employeeTab: route?.query?.employeeTab,
  }),
  (newQuery, oldQuery) => {
    const newEmployeeId = newQuery?.employeeId;
    const oldEmployeeId = oldQuery?.employeeId;
    const newEmployeeTab =
      normalizeOrganizationEmployeeQueryTab(newQuery?.employeeTab) ||
      getOrganizationEmployeeDefaultQueryTab();

    if (newEmployeeId) {
      const employeeIdStr = String(newEmployeeId);
      // Always update if employeeId changes or tab changes
      if (
        !drilldownData.value ||
        drilldownData.value.employeeId !== employeeIdStr ||
        drilldownData.value.tab !== newEmployeeTab
      ) {
        drilldownData.value = {
          employeeId: employeeIdStr,
          tab: newEmployeeTab,
        };
        showTabContent.value = false;
        currentTab.value = "Employees";
      }
    } else if (!newEmployeeId && oldEmployeeId) {
      // Only clear if we're actually removing the employeeId (back to list)
      drilldownData.value = null;
      drilldownBreadcrumbs.value = null;
      showTabContent.value = true;
    }
  },
  { immediate: true, deep: true }
);

const refreshGuides = () => {
  const roleGuide = selectGuide({ key: "what-is-a-role" });
  rolesGuideData.value = roleGuide?.steps?.[0] || null;

  const dashboardGuide = selectGuide({ key: "welcome-as-account-admin" });
  dashboardGuideData.value = dashboardGuide?.steps?.[0] || null;
};

const initializeKnock = () => {
  unsubscribeGuide = subscribeToGuides(refreshGuides);
  refreshGuides();
};

const handleGuideCloseForRoles = async () => {
  try {
    if (rolesGuideData.value && rolesGuideData.value.markAsArchived) {
      await rolesGuideData.value.markAsArchived();
      rolesGuideData.value = null;
    }
  } catch (error) {
    console.error("Error dismissing guide:", error);
  }
};

const loadJobConfiguration = async () => {
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;
  try {
    const { data } = await getAccountData(accountId);
    jobCategoriesOn.value = data?.job_categories_on === true;
    jobFamiliesOn.value = data?.job_families_on === true;
  } catch (error) {
    console.error("Error loading job configuration:", error);
    jobCategoriesOn.value = false;
    jobFamiliesOn.value = false;
  }
};

const loadJobCategoriesCount = async () => {
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;
  try {
    const { data } = await getListOfJobCategories(
      accountId,
      { archived: false },
      { page: 1, pageSize: 1 }
    );
    jobCategoriesCount.value = Number(data && data.count) || 0;
  } catch (error) {
    console.error("Error loading job category count:", error);
    jobCategoriesCount.value = 0;
  }
};

const loadJobFamiliesCount = async () => {
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;
  try {
    const { data } = await getListOfJobFamilies(accountId, {}, { page: 1, pageSize: 1 });
    jobFamiliesCount.value = Number(data && data.count) || 0;
  } catch (error) {
    console.error("Error loading job family count:", error);
    jobFamiliesCount.value = 0;
  }
};

onBeforeMount(async () => {
  await syncAccountProductDetails();
  ideaMdp440Enabled.value = await proxy.$getFlag("idea-MDP-440", false);

  const [ldFlag566, ldFlag581] = await Promise.all([
    proxy.$getFlag(LD_FLAG_IDEA_MDP_566, false),
    proxy.$getFlag(LD_FLAG_IDEA_MDP_581, false),
  ]);

  await loadJobConfiguration();

  // The Categories/Families tabs render only when the LD rollout flag AND the
  // account's job_categories_on / job_families_on setting are both true.
  ideaMdp566Enabled.value = ldFlag566 && jobCategoriesOn.value;
  ideaMdp581Enabled.value = ldFlag581 && jobFamiliesOn.value;

  if (ideaMdp566Enabled.value) {
    await loadJobCategoriesCount();
  }
  if (ideaMdp581Enabled.value) {
    await loadJobFamiliesCount();
  }

  if (drilldownData.value) return;

  const tab = resolveTabFromRoute();
  currentTab.value = tab;
  isResolvingTab.value = false;
});

onMounted(async () => {
  initializeKnock();
});

onBeforeUnmount(() => {
  if (typeof unsubscribeGuide === "function") {
    unsubscribeGuide();
    unsubscribeGuide = null;
  }
});

const menuItems = computed(() =>
  buildOrganizationMenuItems({
    ideaMdp440Enabled: ideaMdp440Enabled.value,
    ideaMdp566Enabled: ideaMdp566Enabled.value,
    ideaMdp581Enabled: ideaMdp581Enabled.value,
    readinessProductEnabled: readinessProductEnabled.value,
    icons: {
      home: Home,
      employee: Employee,
      position: Position,
      role: Role,
      responsibility: Responsibility,
      department: Department,
      category: Category,
      jobFamily: JobFamilyIcon,
    },
  })
);

const handleTabChange = (tabLabel) => {
  // ER-11705 — a tab change that navigates re-creates this page (the router-view
  // is keyed by path), so swapping the tab here first would mount the target
  // tab twice and fire its list requests twice. Let the new page render it.
  let navigates = false;

  if (router) {
    const targetTab = organizationTabSlug(tabLabel);

    // ER-11803 — from a Job Family detail page (/organization/job-families/:id)
    // the sibling OrganizationJobFamilyDetail route is the active match, so a
    // named-route push ({ name: "NewOrganization", params: { tab: "job-families" }})
    // is swallowed. Navigate back to the list by literal path, mirroring
    // ExBreadcrumbs.navigateTo. All other tabs keep the named-route push.
    if (targetTab === "job-families") {
      if (route.path !== "/organization/job-families") {
        navigates = true;
        router.push("/organization/job-families").catch(() => {});
      }
    } else {
      const resolved = router.resolve({
        name: "NewOrganization",
        params: { tab: targetTab },
      });
      if (resolved.route.fullPath !== route.fullPath) {
        navigates = true;
        router
          .push({
            name: "NewOrganization",
            params: { tab: targetTab },
          })
          .catch(() => {});
      }
    }
  }

  if (!navigates) {
    currentTab.value = tabLabel;
    showTabContent.value = true;
    drilldownData.value = null;
  }
  emit("tab-changed", tabLabel);
};

const handleGuideClose = () => {
  emit("guide-closed");
};

const syncAccountProductDetails = async () => {
  try {
    const details = await refreshCurrentAccountDetails(getAvailableApps);
    if (proxy?.$root && details) {
      proxy.$root.currentAccountDetails = details;
      proxy.$root.$emit("currentAccountDetailsUpdated", details);
    }
  } catch {
    // Keep last-known product flags when refresh fails.
  }
};

const handleDrilldown = (data) => {
  drilldownBreadcrumbs.value = data.breadcrumbs || null;

  if (data.employeeId) {
    syncAccountProductDetails();
    const employeeIdStr = String(data.employeeId);
    const employeeTab = normalizeOrganizationEmployeeQueryTab(
      data.tab || getOrganizationEmployeeDefaultQueryTab()
    );

    drilldownData.value = {
      employeeId: employeeIdStr,
      tab: employeeTab,
    };
    showTabContent.value = false;
    currentTab.value = "Employees";

    // Single navigation, synchronously so route is set before child components mount (avoids "Navigation cancelled" and 404)
    if (router) {
      const query = { employeeId: employeeIdStr, employeeTab };
      router
        .push({
          name: "NewOrganization",
          params: { tab: "employees" },
          query,
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated" && err.name !== "NavigationAborted") {
            console.error("OrganizationPage navigation error:", err);
          }
        });
    }
  } else {
    console.warn("OrganizationPage.handleDrilldown: No employeeId in data", data);
  }
};

const handleBackToList = () => {
  showTabContent.value = true;
  drilldownData.value = null;
  drilldownBreadcrumbs.value = null;

  if (router) {
    const targetTab = organizationTabSlug(currentTab.value);
    if (
      route.name !== "NewOrganization" ||
      route.params.tab !== targetTab ||
      Object.keys(route.query).length > 0
    ) {
      router
        .push({
          name: "NewOrganization",
          params: { tab: targetTab },
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.organization-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
}

.tab-content {
  flex: 1;
  width: 100%;
}

.organization-tab-skeleton {
  padding: 16px;
}

.organization-tab-skeleton__bar {
  height: 16px;
  border-radius: 4px;
  background-color: #eaecf0;
  margin-bottom: 12px;
}

.organization-tab-skeleton__bar--medium {
  width: 75%;
}

.organization-tab-skeleton__bar--short {
  width: 45%;
  margin-bottom: 0;
}

.organization-page-title-container {
  padding: 24px 0px 0px 0px;
}

.organization-page-title-row {
  display: flex;
  align-items: center;
  gap: 7px;
}

.organization-page-title-row__title {
  flex: 1;
  min-width: 0;
}

.drilldown-content {
  flex: 1;
  width: 100%;
}
.responsibilities-tab-container {
  padding-bottom: 60px !important;
}
.role-tab-container {
  padding-bottom: 80px !important;
}
.employees-tab-container {
  padding-bottom: 80px !important;
}
</style>
